import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ShieldAlert, X, RefreshCw, ExternalLink, Eye, MapPin, Store, Layers, Clock } from "lucide-react";
import { useT52 } from "@/i18n/v52";

function Metric({ label, value, testid }) {
  return <div className="f52-val raised" data-testid={testid}><dt>{label}</dt><dd>{value ?? "—"}</dd></div>;
}

function fmtRemaining(ms, t52) {
  if (ms == null) return "—";
  if (ms <= 0) return t52("cq_expired");
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${String(s).padStart(2, "0")} ${t52("cq_min")}`;
}

const addr = (l) => [l.street, `${l.postal_code || ""} ${l.city || ""}`.trim(), l.canton].filter(Boolean).join(", ");
const hours = (h) => (Array.isArray(h) ? h.map((p) => (Array.isArray(p) ? p.join(": ") : p)).join("\n") : h);

function Dl({ rows }) {
  return <dl className="f52-dl">{rows.map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v || "—"}</dd></div>)}</dl>;
}

// Read-only Cockpit-Kurzansicht (Design 5.2). Logik/Session-Vertrag unverändert.
export function CockpitQuickView({ tenants = [], initialTenantId, onClose }) {
  const { t52, tOr } = useT52();
  const t52Ref = useRef(t52);
  t52Ref.current = t52;
  const [tenantId, setTenantId] = useState(initialTenantId);
  const [session, setSession] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState("all");
  const [remaining, setRemaining] = useState(null);
  const [fallbackUrl, setFallbackUrl] = useState(null);
  const tokenRef = useRef(null);
  const jtiRef = useRef(null);
  const closeRef = useRef(null);

  const hdr = () => ({ headers: { "X-Support-Token": tokenRef.current } });

  const loadOverview = useCallback(async () => {
    const o = await api.get("/cockpit/overview", hdr());
    setData(o.data);
    setSel("all");
  }, []);

  const openTenant = useCallback(async (nextTenantId) => {
    setLoading(true); setErr(null); setData(null);
    try {
      const prevJti = jtiRef.current;
      const { data: res } = await api.post("/cockpit/quickview/open", {
        tenant_id: nextTenantId, previous_jti: prevJti || undefined,
      });
      tokenRef.current = res.support_token;
      jtiRef.current = res.session.jti;
      setSession(res.session);
      setTenantId(nextTenantId);
      await loadOverview();
    } catch (e) {
      setErr(typeof e.response?.data?.detail === "string" ? e.response.data.detail : t52Ref.current("cq_loadErr"));
    } finally {
      setLoading(false);
    }
  }, [loadOverview]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { openTenant(initialTenantId); closeRef.current?.focus(); }, []);

  useEffect(() => {
    if (!session?.expires_at) return;
    const end = new Date(session.expires_at).getTime();
    const tick = () => setRemaining(end - Date.now());
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [session]);

  const endSession = useCallback(async (silent) => {
    const t = tokenRef.current;
    if (t) {
      try { await api.post("/cockpit/session/end", {}, { headers: { "X-Support-Token": t } }); } catch { /* ignore */ }
    }
    tokenRef.current = null; jtiRef.current = null;
    if (!silent) toast.success(t52Ref.current("cq_ended"));
  }, []);

  // revoke on unmount (tab/panel closed)
  useEffect(() => () => { const t = tokenRef.current; if (t) { api.post("/cockpit/session/end", {}, { headers: { "X-Support-Token": t } }).catch(() => {}); } }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") handleClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, []);

  const handleClose = async () => { await endSession(true); onClose?.(); };
  const handleReload = async () => { try { await loadOverview(); toast.success(t52("cq_reloaded")); } catch { toast.error(t52("cq_reloadFail")); } };
  const handleNewTab = () => {
    // Tab synchron im Klick öffnen (kein Popup-Blocker); Server-302 erzeugt pro Klick einen frischen Single-Use-Launch.
    if (!jtiRef.current || loading) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/cockpit/quickview/launch-redirect?jti=${encodeURIComponent(jtiRef.current)}`;
    let win = null;
    try { win = window.open(url, "_blank"); } catch { win = null; }
    if (win) { try { win.opener = null; } catch { /* noop */ } return; }
    setFallbackUrl(url);
  };
  const handleSwitch = (e) => { const id = e.target.value; if (id && id !== tenantId) openTenant(id); };

  const org = data?.organization;
  const isMulti = !!data?.is_multi;
  const shown = !isMulti || sel === "all" ? null : data?.locations?.find((l) => l.id === sel);

  return (
    <div data-testid="cockpit-quickview" className="f52-cq" role="dialog" aria-modal="true" aria-label={t52("cq_aria")}>
      <button type="button" data-testid="cockpit-qv-backdrop" aria-label={t52("cq_close")} tabIndex={-1} onClick={handleClose} className="f52-cq-backdrop" />
      <div className="f52-cq-sheet">
        <header data-testid="cockpit-qv-header" className="f52-cq-head">
          <div className="f52-cq-row1">
            <span data-testid="cockpit-qv-superadmin" className="f52-cq-sa"><Eye aria-hidden="true" />{t52("cq_sa")}</span>
            <strong data-testid="cockpit-qv-company" className="f52-cq-co">{session?.company_name || org?.company_name || "—"}</strong>
            <span data-testid="cockpit-qv-plan" className={`f52-plan ${session?.plan || ""}`}>{session?.plan || "—"}</span>
            <span data-testid="cockpit-qv-mode" className="f52-cq-chip">{t52((session?.account_mode || org?.account_mode) === "multi" ? "kd_multi" : "kd_single")}</span>
            <span data-testid="cockpit-qv-tenant" className="f52-cq-chip mono">{tenantId}</span>
            <span data-testid="cockpit-qv-readonly" className="f52-cq-chip">{t52("cq_ro")}</span>
            <span data-testid="cockpit-qv-remaining" className="f52-cq-chip"><Clock aria-hidden="true" />{fmtRemaining(remaining, t52)}</span>
          </div>
          <div className="f52-cq-row2">
            <label className="f52-sr" htmlFor="cockpit-qv-switch">{t52("cq_switch")}</label>
            <select id="cockpit-qv-switch" data-testid="cockpit-qv-switch" value={tenantId} onChange={handleSwitch}>
              {tenants.map((t) => (<option key={t.tenant_id} value={t.tenant_id}>{t.company_name || t.tenant_id}</option>))}
            </select>
            <button type="button" data-testid="cockpit-qv-reload" onClick={handleReload} className="f52-cq-btn"><RefreshCw aria-hidden="true" />{t52("cq_reload")}</button>
            <button type="button" data-testid="cockpit-qv-newtab" onClick={handleNewTab} disabled={loading} className="f52-cq-btn"><ExternalLink aria-hidden="true" />{t52("c_newTab")}</button>
            <button type="button" ref={closeRef} data-testid="cockpit-qv-close" onClick={handleClose} className="f52-cq-btn f52-cq-close"><X aria-hidden="true" />{t52("cq_close")}</button>
          </div>
        </header>

        <div className="f52-cq-body">
          {loading ? (
            <div data-testid="cockpit-qv-loading" className="f52-empty neutral" aria-busy="true"><span className="ico"><Loader2 aria-hidden="true" className="f52-spin" /></span></div>
          ) : err ? (
            <div data-testid="cockpit-qv-error" className="f52-empty warn" role="alert">
              <span className="ico"><ShieldAlert aria-hidden="true" /></span>
              <h3>{err}</h3>
              <button type="button" onClick={handleClose} className="f52-btn f52-btn-primary"><X aria-hidden="true" />{t52("cq_close")}</button>
            </div>
          ) : org ? (
            <div className="f52-cq-inner">
              <div className="f52-cq-title">
                <span className="f52-tile">{isMulti ? <Layers aria-hidden="true" /> : <Store aria-hidden="true" />}</span>
                <div>
                  <h2 data-testid="cockpit-qv-title">{isMulti ? t52("cq_multi") : "Cockpit"}</h2>
                  <p>{org.company_name}{org.city ? ` · ${org.city}` : ""}{data.is_qa ? " · INTERNAL QA" : ""}</p>
                </div>
              </div>

              {isMulti && (
                <div data-testid="cockpit-qv-location-switcher" className="f52-cq-locs">
                  <button type="button" data-testid="cockpit-qv-loc-all" aria-pressed={sel === "all"} onClick={() => setSel("all")} className="f52-cq-loc">{t52("cq_allLocs")}</button>
                  {data.locations.map((l) => (
                    <button type="button" key={l.id} data-testid={`cockpit-qv-loc-${l.id}`} aria-pressed={sel === l.id} onClick={() => setSel(l.id)} className="f52-cq-loc">{l.name}</button>
                  ))}
                </div>
              )}

              {(!isMulti || sel === "all") && (
                <dl className="f52-vals f52-cq-vals" data-testid="cockpit-qv-metrics">
                  <Metric label={t52("s_locs")} value={isMulti ? data.locations.length : 1} testid="cockpit-qv-metric-locations" />
                  <Metric label={t52("cq_listings")} value={org.item_count} testid="cockpit-qv-metric-items" />
                  <Metric label={t52("s_inq")} value={org.inquiry_count} testid="cockpit-qv-metric-inquiries" />
                  <Metric label={t52("f_status")} value={org.status ? tOr(`orgst_${org.status}`, org.status) : "—"} testid="cockpit-qv-metric-status" />
                </dl>
              )}

              {isMulti && shown ? (
                <section data-testid="cockpit-qv-location-detail" className="f52-panel f52-cq-card">
                  <h3><MapPin aria-hidden="true" className="f52-inline-ico" />{shown.name}</h3>
                  <Dl rows={[[t52("f_address"), addr(shown)], [t52("f_phone"), shown.phone], [t52("f_email"), shown.email], [t52("f_hours"), hours(shown.opening_hours)]]} />
                </section>
              ) : (
                <section data-testid={isMulti ? "cockpit-qv-all-locations" : "cockpit-qv-single"} className="f52-panel f52-cq-card">
                  <h3>{t52(isMulti ? "cq_allLocs" : "f_location")}</h3>
                  {isMulti ? (
                    <ul className="f52-cq-list">
                      {data.locations.map((l) => (
                        <li key={l.id}>
                          <div><strong>{l.name}</strong><div className="f52-data-note">{[l.street, `${l.postal_code || ""} ${l.city || ""}`.trim()].filter(Boolean).join(", ")}</div></div>
                          <button type="button" onClick={() => setSel(l.id)} className="f52-btn f52-btn-secondary">{t52("k_open")}</button>
                        </li>
                      ))}
                      {!data.locations.length && <li className="f52-data-note">{t52("cq_noLocs")}</li>}
                    </ul>
                  ) : (
                    <Dl rows={[[t52("f_company"), org.company_name], [t52("f_city"), org.city], [t52("f_plan"), org.plan], [t52("f_website"), org.external_website]]} />
                  )}
                </section>
              )}

              <p className="f52-cq-foot">{t52("cq_foot")}</p>
            </div>
          ) : null}
        </div>
      </div>

      {fallbackUrl && (
        <div data-testid="cockpit-qv-fallback" className="f52-dlg" role="alertdialog" aria-modal="true" aria-labelledby="cq-fb-t">
          <div className="box">
            <h2 id="cq-fb-t">{t52("cq_fbT")}</h2>
            <p>{t52("cq_fbD")}</p>
            <div className="acts">
              <button type="button" data-testid="cockpit-qv-fallback-cancel" onClick={() => setFallbackUrl(null)} className="f52-btn f52-btn-secondary">{t52("c_cancel")}</button>
              <a data-testid="cockpit-qv-fallback-open" href={fallbackUrl} target="_blank" rel="noopener noreferrer" onClick={() => setTimeout(() => setFallbackUrl(null), 400)} className="f52-btn f52-btn-primary"><ExternalLink aria-hidden="true" />{t52("cq_fbT")}</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
